import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useController, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Button,
  CurrencyInput,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  StatusBadge,
} from '@payment-plan/ui';
import type { CatalogUnit } from '../../../types/admin';
import { formatCOP, toPesos } from '../../separations/money';

/**
 * HU-A06 · paso 1 de 2: capturar el nuevo precio de lista (RHF + Zod, pesos enteros en `bigint`,
 * AGENTS §15). El paso 2 es el `ConfirmDialog` con «anterior → nuevo» (`CatalogUnits`).
 * El tope es `Number.MAX_SAFE_INTEGER` porque `PATCH /units/:id/price` recibe un entero JSON (feat-18).
 */
const MAXIMO = BigInt(Number.MAX_SAFE_INTEGER);

function esquema(actual: bigint) {
  return z.object({
    precio: z
      .bigint()
      .refine((v) => v > 0n, 'Ingrese un precio mayor que cero.')
      .refine((v) => v <= MAXIMO, 'El precio supera el máximo que se puede registrar.')
      .refine((v) => v !== actual, 'El nuevo precio es igual al actual.'),
  });
}

interface Valores {
  precio: bigint;
}

export function etiquetaUnidad(unidad: Pick<CatalogUnit, 'torre' | 'numero'>): string {
  return `${unidad.torre} · ${unidad.numero}`;
}

interface PriceAdjustDialogProps {
  unidad: CatalogUnit | null;
  onOpenChange: (open: boolean) => void;
  onReview: (unidad: CatalogUnit, nuevo: bigint) => void;
}

export function PriceAdjustDialog({ unidad, onOpenChange, onReview }: PriceAdjustDialogProps): React.JSX.Element {
  const actual = toPesos(unidad?.precioLista);
  const schema = React.useMemo(() => esquema(actual), [actual]);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Valores>({ resolver: zodResolver(schema), defaultValues: { precio: 0n } });
  // `useController` (no `<Controller>`): el input es hijo directo de `FormField`, que le asocia el
  // error con `aria-describedby` y `aria-invalid` (Web 02).
  const { field } = useController({ control, name: 'precio' });

  React.useEffect(() => {
    if (unidad) reset({ precio: 0n });
  }, [unidad, reset]);

  const onSubmit = handleSubmit((valores) => {
    if (unidad) onReview(unidad, valores.precio);
  });

  return (
    <Dialog open={Boolean(unidad)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajustar precio de lista</DialogTitle>
          <DialogDescription>
            El nuevo precio aplica solo a separaciones futuras; las ya generadas conservan el precio con el que se
            emitieron.
          </DialogDescription>
        </DialogHeader>
        {unidad && (
          <form onSubmit={(e) => void onSubmit(e)} noValidate className="space-y-4">
            <dl className="grid grid-cols-2 gap-3 rounded-lg border border-border/70 bg-surface-fade p-4 text-sm">
              <div>
                <dt className="text-muted-foreground">Unidad</dt>
                <dd className="font-medium text-foreground">{etiquetaUnidad(unidad)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Estado</dt>
                <dd>
                  <StatusBadge status={unidad.estado} />
                </dd>
              </div>
              <div className="col-span-2">
                <dt className="text-muted-foreground">Precio actual</dt>
                <dd className="text-base font-semibold tabular-nums text-foreground">{formatCOP(unidad.precioLista)}</dd>
              </div>
            </dl>
            <FormField label="Nuevo precio de lista" htmlFor="nuevo-precio" required error={errors.precio?.message}>
              <CurrencyInput
                id="nuevo-precio"
                className="h-11"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={Boolean(errors.precio)}
              />
            </FormField>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit">Revisar ajuste</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
